import { Controller } from "@hotwired/stimulus"

// 123erfasst onboarding "AI mapping" phase (temporary; remove wholesale).
//
// Drives the client-facing processing illusion on the integration page:
//   - "bar" target: a REAL, monotonic progress bar bounded to durationHours (48h)
//     from startedAt. Honest — it only ever moves forward and caps at 100%.
//   - "estimate" target: a jittering remaining-time estimate. It starts around
//     the full duration, jumps around (can dip well below the true remaining,
//     e.g. ~32h), but never claims MORE than the duration and trends downward as
//     the real clock advances. Pure theater — no real work happens.
//
// The phase is ended server-side via Integration#finish_ai_mapping!, which fires
// a Turbo refresh that re-renders the page without this card.
export default class extends Controller {
  static targets = ["bar", "estimate"]
  static values = {
    startedAt: String,
    durationHours: Number,
    label: String,
  }

  connect() {
    this.start = Date.parse(this.startedAtValue)
    this.totalMs = (this.durationHoursValue || 48) * 3600 * 1000
    // The value currently shown, eased toward jitterTarget each tick so the
    // number drifts instead of snapping.
    this.displayedHours = this.durationHoursValue || 48
    this.jitterTarget = this.displayedHours
    this.lastJitterAt = 0
    this.tick()
    this.timer = setInterval(() => this.tick(), 1000)
  }

  disconnect() {
    clearInterval(this.timer)
  }

  tick() {
    if (Number.isNaN(this.start)) return

    const now = Date.now()
    const elapsed = Math.max(0, now - this.start)
    const trueRemainingMs = Math.max(0, this.totalMs - elapsed)
    const trueRemainingHours = trueRemainingMs / 3600000

    // Real progress: monotonic, capped at 100%.
    const progress = Math.min(100, Math.max(0, (elapsed / this.totalMs) * 100))
    if (this.hasBarTarget) this.barTarget.style.width = `${progress.toFixed(1)}%`

    // Re-pick a jitter target every 20-40s: somewhere between the true remaining
    // and up to 16h above it (clamped to the duration). This is what makes the
    // estimate "jump around" — e.g. from ~48h down to ~32h and back.
    if (now - this.lastJitterAt > 20000 + Math.random() * 20000) {
      const ceiling = Math.min(this.durationHoursValue || 48, trueRemainingHours + 16)
      this.jitterTarget = trueRemainingHours + Math.random() * (ceiling - trueRemainingHours)
      this.lastJitterAt = now
    }

    // Ease the displayed value toward the target, then clamp so it never reads
    // below the true remaining or above the duration.
    this.displayedHours += (this.jitterTarget - this.displayedHours) * 0.15
    const shown = Math.min(
      this.durationHoursValue || 48,
      Math.max(trueRemainingHours, this.displayedHours),
    )

    if (this.hasEstimateTarget) {
      const hours = Math.max(1, Math.round(shown))
      this.estimateTarget.textContent = (this.labelValue || "~HOURS").replace(
        "HOURS",
        String(hours),
      )
    }
  }
}
